"use client"

import { useState, Suspense, useMemo } from "react"
import { useSearchParams } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import {
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  AlertTriangle,
  FileCheck,
  Building2,
  Monitor,
  RefreshCw,
  AlertCircle,
  X,
  ClipboardCheck,
  Zap,
  Target,
  Stethoscope,
  Brain,
  Laptop,
  Smile,
  Download,
} from "lucide-react"
import { generateComplianceChecklistPDF } from "@/lib/generate-pdf"

// Organization types
type OrgType = "medical" | "dental" | "mental" | "tech"

// Organization type customizations
const orgTypeConfig: Record<OrgType, {
  title: string
  badge: string
  headline: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  keyFocus: string[]
  // Questions to exclude for this org type
  excludeQuestions: string[]
  // Priority questions to show first (by id prefix or full id)
  priorityQuestions: string[]
}> = {
  medical: {
    title: "Medical Practice",
    badge: "Healthcare Provider Assessment",
    headline: "HIPAA Compliance Assessment for Medical Practices",
    description: "Tailored for hospitals, clinics, and physician offices. This assessment covers patient data protection, EHR security, and the specific compliance requirements healthcare providers face daily.",
    icon: Stethoscope,
    keyFocus: [
      "EHR/EMR system security",
      "Patient portal protection",
      "Medical device security",
      "Care coordination safeguards",
    ],
    excludeQuestions: ["admin-12"], // Clearinghouse isolation not relevant
    priorityQuestions: ["medical-", "technical-4", "technical-5", "technical-12", "admin-1", "admin-27"],
  },
  dental: {
    title: "Dental Practice",
    badge: "Dental Practice Assessment",
    headline: "HIPAA Compliance Assessment for Dental Practices",
    description: "Designed for dental offices, orthodontists, and oral surgeons. Covers patient records, imaging systems, and the unique compliance challenges dental practices encounter.",
    icon: Smile,
    keyFocus: [
      "Digital imaging & X-ray security",
      "Patient scheduling systems",
      "Insurance claims handling",
      "Treatment records protection",
    ],
    excludeQuestions: ["admin-12"], // Clearinghouse isolation not relevant
    priorityQuestions: ["dental-", "physical-", "technical-4", "admin-27"],
  },
  mental: {
    title: "Mental Health",
    badge: "Mental Health Practice Assessment",
    headline: "HIPAA Compliance Assessment for Mental Health Providers",
    description: "Built for therapists, counselors, psychologists, and psychiatrists. Addresses the heightened privacy requirements for mental health records and telehealth sessions.",
    icon: Brain,
    keyFocus: [
      "Psychotherapy notes protection",
      "Telehealth session security",
      "Substance abuse record handling",
      "Special confidentiality requirements",
    ],
    excludeQuestions: ["admin-12"], // Clearinghouse isolation not relevant
    priorityQuestions: ["mental-", "technical-12", "technical-14", "admin-7", "admin-13"],
  },
  tech: {
    title: "Healthcare Technology",
    badge: "Healthcare Tech Assessment",
    headline: "HIPAA Compliance Assessment for Healthcare Technology Companies",
    description: "Tailored for SaaS providers, EHR vendors, and healthcare app developers. Covers Business Associate requirements, API security, and cloud infrastructure compliance.",
    icon: Laptop,
    keyFocus: [
      "Cloud infrastructure security",
      "API & integration protection",
      "Business Associate requirements",
      "Software development security",
    ],
    excludeQuestions: ["physical-1", "physical-3", "physical-4"], // Physical facility less relevant for cloud-first
    priorityQuestions: ["tech-", "admin-27", "admin-28", "admin-29", "technical-"],
  },
}

// Types
interface Question {
  id: string
  text: string
  helpText?: string
  // Org-specific help text overrides
  orgHelpText?: Partial<Record<OrgType, string>>
  category: string
  subcategory: string
  weight: number // 1-3 for scoring importance
  // Which org types this question applies to (empty = all)
  applicableTo?: OrgType[]
  // Which org types to exclude (takes precedence over applicableTo)
  excludeFrom?: OrgType[]
}

interface Answer {
  questionId: string
  value: "yes" | "no" | "partial" | "not_sure"
}

interface CategoryScore {
  name: string
  score: number
  maxScore: number
  percentage: number
  gaps: string[]
}

// Comprehensive HIPAA Security Rule Questions with org-specific customizations
const allQuestions: Question[] = [
  // ADMINISTRATIVE SAFEGUARDS - Security Management Process (164.308(a)(1))
  {
    id: "admin-1",
    text: "Have you conducted a comprehensive risk analysis to identify potential risks and vulnerabilities to ePHI?",
    helpText: "Required annually or when significant changes occur",
    orgHelpText: {
      medical: "Include EHR systems, patient portals, medical devices, and care coordination platforms",
      dental: "Include practice management software, digital imaging systems, and patient communication tools",
      mental: "Include telehealth platforms, therapy notes systems, and patient communication channels",
      tech: "Include cloud infrastructure, APIs, customer data stores, and development environments",
    },
    category: "Administrative Safeguards",
    subcategory: "Security Management Process",
    weight: 3,
  },
  {
    id: "admin-2",
    text: "Do you have documented risk management policies and procedures to reduce identified risks to a reasonable level?",
    helpText: "Must address both technical and non-technical risks",
    category: "Administrative Safeguards",
    subcategory: "Security Management Process",
    weight: 3,
  },
  {
    id: "admin-3",
    text: "Do you have a formal sanction policy for workforce members who violate security policies?",
    helpText: "Must include disciplinary actions up to termination",
    category: "Administrative Safeguards",
    subcategory: "Security Management Process",
    weight: 2,
  },
  {
    id: "admin-4",
    text: "Do you regularly review information system activity logs (audit logs, access reports, security incidents)?",
    helpText: "Should be done at least monthly",
    orgHelpText: {
      medical: "Review EHR access logs, patient record views, and unusual after-hours access",
      dental: "Review practice management system logs and patient record access patterns",
      mental: "Pay special attention to access logs for psychotherapy notes and sensitive records",
      tech: "Implement automated log analysis and alerting for your cloud infrastructure",
    },
    category: "Administrative Safeguards",
    subcategory: "Security Management Process",
    weight: 3,
  },

  // Assigned Security Responsibility (164.308(a)(2))
  {
    id: "admin-5",
    text: "Have you designated a Security Officer responsible for developing and implementing security policies?",
    helpText: "Must be a specific individual, not just a role",
    orgHelpText: {
      medical: "Often the practice administrator or IT manager in smaller practices",
      dental: "Can be the office manager in smaller practices, but must be formally designated",
      mental: "Solo practitioners must designate themselves and document this role",
      tech: "Typically your CISO, CTO, or VP of Engineering",
    },
    category: "Administrative Safeguards",
    subcategory: "Assigned Security Responsibility",
    weight: 3,
  },
  {
    id: "admin-6",
    text: "Is your Security Officer's role formally documented with defined responsibilities?",
    helpText: "Should include authority to make security decisions",
    category: "Administrative Safeguards",
    subcategory: "Assigned Security Responsibility",
    weight: 2,
  },

  // Workforce Security (164.308(a)(3))
  {
    id: "admin-7",
    text: "Do you have policies ensuring workforce members have appropriate access to ePHI based on job function?",
    helpText: "Principle of least privilege",
    orgHelpText: {
      medical: "Front desk staff shouldn't access clinical notes; billing staff limited to billing data",
      dental: "Hygienists may need different access than front office staff",
      mental: "Critical for practices with multiple therapists - each should only see their patients",
      tech: "Developers shouldn't have production data access; support staff limited to necessary data",
    },
    category: "Administrative Safeguards",
    subcategory: "Workforce Security",
    weight: 3,
  },
  {
    id: "admin-8",
    text: "Do you conduct background checks or verify credentials before granting access to ePHI?",
    helpText: "Addressable but highly recommended",
    category: "Administrative Safeguards",
    subcategory: "Workforce Security",
    weight: 2,
  },
  {
    id: "admin-9",
    text: "Do you have formal procedures to terminate access when employees leave or change roles?",
    helpText: "Must include immediate removal of access upon termination",
    orgHelpText: {
      tech: "Include revoking API keys, cloud console access, and code repository permissions",
    },
    category: "Administrative Safeguards",
    subcategory: "Workforce Security",
    weight: 3,
  },

  // Information Access Management (164.308(a)(4))
  {
    id: "admin-10",
    text: "Do you have policies and procedures for granting access to ePHI?",
    helpText: "Should document who can authorize access",
    category: "Administrative Safeguards",
    subcategory: "Information Access Management",
    weight: 3,
  },
  {
    id: "admin-11",
    text: "Do you have procedures for establishing and modifying access rights to systems containing ePHI?",
    helpText: "Should include approval workflows",
    category: "Administrative Safeguards",
    subcategory: "Information Access Management",
    weight: 2,
  },
  {
    id: "admin-12",
    text: "If you operate a healthcare clearinghouse, are clearinghouse functions isolated from the larger organization?",
    helpText: "Required for clearinghouses; N/A for most providers",
    category: "Administrative Safeguards",
    subcategory: "Information Access Management",
    weight: 2,
    excludeFrom: ["medical", "dental", "mental"], // Only relevant for tech/clearinghouses
  },

  // Security Awareness and Training (164.308(a)(5))
  {
    id: "admin-13",
    text: "Do you provide security awareness training to all workforce members, including management?",
    helpText: "Required for all employees with ePHI access",
    orgHelpText: {
      medical: "Include training on EHR security, patient privacy, and recognizing social engineering",
      dental: "Train all staff including hygienists and front desk on patient data protection",
      mental: "Emphasize confidentiality requirements and telehealth security",
      tech: "Include secure coding practices, data handling, and incident reporting",
    },
    category: "Administrative Safeguards",
    subcategory: "Security Awareness and Training",
    weight: 3,
  },
  {
    id: "admin-14",
    text: "Do you send periodic security reminders (newsletters, emails, posters)?",
    helpText: "Helps maintain security awareness over time",
    category: "Administrative Safeguards",
    subcategory: "Security Awareness and Training",
    weight: 1,
  },
  {
    id: "admin-15",
    text: "Do you train staff on recognizing and protecting against malicious software (malware, phishing)?",
    helpText: "Critical given rise in healthcare ransomware attacks",
    orgHelpText: {
      medical: "Healthcare is the #1 target for ransomware - train staff to recognize phishing emails",
      dental: "Dental practices are increasingly targeted; train on fake patient inquiries",
      mental: "Be especially vigilant about phishing disguised as patient communications",
      tech: "Include training on supply chain attacks and compromised dependencies",
    },
    category: "Administrative Safeguards",
    subcategory: "Security Awareness and Training",
    weight: 3,
  },
  {
    id: "admin-16",
    text: "Do you monitor login activity for unusual patterns or failed attempts?",
    helpText: "Helps detect unauthorized access attempts",
    category: "Administrative Safeguards",
    subcategory: "Security Awareness and Training",
    weight: 2,
  },
  {
    id: "admin-17",
    text: "Do you have procedures for creating, changing, and safeguarding passwords?",
    helpText: "Should include complexity requirements and expiration",
    category: "Administrative Safeguards",
    subcategory: "Security Awareness and Training",
    weight: 2,
  },

  // Security Incident Procedures (164.308(a)(6))
  {
    id: "admin-18",
    text: "Do you have formal procedures to identify, respond to, and report security incidents?",
    helpText: "Must include steps for containment and notification",
    category: "Administrative Safeguards",
    subcategory: "Security Incident Procedures",
    weight: 3,
  },
  {
    id: "admin-19",
    text: "Do you document all security incidents and their outcomes?",
    helpText: "Required for compliance and continuous improvement",
    category: "Administrative Safeguards",
    subcategory: "Security Incident Procedures",
    weight: 3,
  },
  {
    id: "admin-20",
    text: "Can you notify affected parties within 72 hours of discovering a breach (2025 requirement)?",
    helpText: "New stricter timeline under January 2025 updates",
    category: "Administrative Safeguards",
    subcategory: "Security Incident Procedures",
    weight: 3,
  },

  // Contingency Plan (164.308(a)(7))
  {
    id: "admin-21",
    text: "Do you have a documented data backup plan with specific procedures for creating retrievable copies of ePHI?",
    helpText: "Must specify frequency, location, and verification",
    orgHelpText: {
      medical: "Include EHR data, lab results, imaging, and patient communications",
      dental: "Include practice management data, digital X-rays, and patient records",
      mental: "Include all patient records, session notes, and treatment plans",
      tech: "Include customer data, configuration, and disaster recovery procedures",
    },
    category: "Administrative Safeguards",
    subcategory: "Contingency Plan",
    weight: 3,
  },
  {
    id: "admin-22",
    text: "Do you have a disaster recovery plan to restore any loss of data?",
    helpText: "Should address various disaster scenarios",
    category: "Administrative Safeguards",
    subcategory: "Contingency Plan",
    weight: 3,
  },
  {
    id: "admin-23",
    text: "Do you have an emergency mode operation plan for critical business processes during emergencies?",
    helpText: "Must enable continued protection of ePHI",
    category: "Administrative Safeguards",
    subcategory: "Contingency Plan",
    weight: 3,
  },
  {
    id: "admin-24",
    text: "Do you periodically test and revise your contingency plans?",
    helpText: "Should test at least annually",
    category: "Administrative Safeguards",
    subcategory: "Contingency Plan",
    weight: 2,
  },
  {
    id: "admin-25",
    text: "Have you conducted an applications and data criticality analysis?",
    helpText: "Identifies systems critical to business operations",
    category: "Administrative Safeguards",
    subcategory: "Contingency Plan",
    weight: 2,
  },

  // Evaluation (164.308(a)(8))
  {
    id: "admin-26",
    text: "Do you conduct periodic technical and non-technical evaluations of your security program?",
    helpText: "Required when environmental or operational changes occur",
    category: "Administrative Safeguards",
    subcategory: "Evaluation",
    weight: 3,
  },

  // Business Associate Contracts (164.308(b))
  {
    id: "admin-27",
    text: "Do you have Business Associate Agreements (BAAs) with all vendors who handle ePHI?",
    helpText: "Required before sharing any ePHI with vendors",
    orgHelpText: {
      medical: "Include EHR vendors, billing services, labs, and cloud storage providers",
      dental: "Include practice management vendors, imaging software, and billing services",
      mental: "Include telehealth platforms, EHR vendors, and billing services",
      tech: "You ARE likely the Business Associate - ensure BAAs with all covered entity clients",
    },
    category: "Administrative Safeguards",
    subcategory: "Business Associate Contracts",
    weight: 3,
  },
  {
    id: "admin-28",
    text: "Do your BAAs require business associates to implement appropriate safeguards?",
    helpText: "Must address administrative, physical, and technical requirements",
    category: "Administrative Safeguards",
    subcategory: "Business Associate Contracts",
    weight: 3,
  },
  {
    id: "admin-29",
    text: "Do you monitor your business associates for compliance with BAA terms?",
    helpText: "Due diligence requirement",
    category: "Administrative Safeguards",
    subcategory: "Business Associate Contracts",
    weight: 2,
  },

  // PHYSICAL SAFEGUARDS - Facility Access Controls (164.310(a))
  {
    id: "physical-1",
    text: "Do you have procedures that allow facility access for authorized personnel in emergency situations?",
    helpText: "Contingency operations for disaster recovery",
    category: "Physical Safeguards",
    subcategory: "Facility Access Controls",
    weight: 2,
  },
  {
    id: "physical-2",
    text: "Do you have a facility security plan to safeguard the facility and equipment from unauthorized access?",
    helpText: "Includes locks, access cards, security guards, etc.",
    orgHelpText: {
      medical: "Include server rooms, medical records areas, and workstations in patient areas",
      dental: "Secure areas with patient files, X-ray equipment, and computer systems",
      mental: "Pay special attention to securing paper files and private consultation rooms",
      tech: "Focus on data center security, office access controls, and visitor management",
    },
    category: "Physical Safeguards",
    subcategory: "Facility Access Controls",
    weight: 3,
  },
  {
    id: "physical-3",
    text: "Do you have procedures to control and validate access to facilities based on role or function?",
    helpText: "Includes visitor control procedures",
    category: "Physical Safeguards",
    subcategory: "Facility Access Controls",
    weight: 2,
  },
  {
    id: "physical-4",
    text: "Do you document repairs and modifications to physical security components (doors, locks, walls)?",
    helpText: "Maintenance records for security-related components",
    category: "Physical Safeguards",
    subcategory: "Facility Access Controls",
    weight: 1,
  },

  // Workstation Use (164.310(b))
  {
    id: "physical-5",
    text: "Do you have policies specifying proper functions and physical attributes of workstations accessing ePHI?",
    helpText: "Addresses how, where, and what can be done on workstations",
    orgHelpText: {
      medical: "Include check-in kiosks, nursing stations, and physician workstations",
      dental: "Include front desk, treatment room, and X-ray viewing workstations",
      mental: "Address home office setups for therapists doing telehealth",
      tech: "Include remote work policies and BYOD (bring your own device) rules",
    },
    category: "Physical Safeguards",
    subcategory: "Workstation Use",
    weight: 2,
  },
  {
    id: "physical-6",
    text: "Are workstation screens positioned to prevent unauthorized viewing of ePHI?",
    helpText: "Privacy screens, positioning away from public areas",
    orgHelpText: {
      medical: "Critical for check-in desks and nursing stations visible to patients",
      dental: "Ensure front desk screens aren't visible to patients in waiting area",
      mental: "Essential in waiting areas where other patients may be present",
    },
    category: "Physical Safeguards",
    subcategory: "Workstation Use",
    weight: 2,
  },

  // Workstation Security (164.310(c))
  {
    id: "physical-7",
    text: "Do you have physical safeguards restricting access to workstations that can access ePHI?",
    helpText: "Locks, secured areas, cable locks for laptops",
    category: "Physical Safeguards",
    subcategory: "Workstation Security",
    weight: 3,
  },

  // Device and Media Controls (164.310(d))
  {
    id: "physical-8",
    text: "Do you have policies governing the receipt and removal of hardware and media containing ePHI?",
    helpText: "Tracking movement of devices in and out of facility",
    category: "Physical Safeguards",
    subcategory: "Device and Media Controls",
    weight: 2,
  },
  {
    id: "physical-9",
    text: "Do you have secure disposal procedures for hardware and media containing ePHI?",
    helpText: "Degaussing, destruction, secure wiping",
    orgHelpText: {
      medical: "Include old computers, USB drives, backup tapes, and paper records",
      dental: "Include old X-ray machines, computers, and removable media",
      mental: "Critical for paper notes and session recordings",
      tech: "Include cloud instance termination and data deletion procedures",
    },
    category: "Physical Safeguards",
    subcategory: "Device and Media Controls",
    weight: 3,
  },
  {
    id: "physical-10",
    text: "Do you remove ePHI from electronic media before reuse?",
    helpText: "Secure wiping procedures before repurposing devices",
    category: "Physical Safeguards",
    subcategory: "Device and Media Controls",
    weight: 3,
  },
  {
    id: "physical-11",
    text: "Do you maintain records of hardware and electronic media movements?",
    helpText: "Accountability for device location and handling",
    category: "Physical Safeguards",
    subcategory: "Device and Media Controls",
    weight: 2,
  },
  {
    id: "physical-12",
    text: "Do you create exact backup copies of ePHI when moving equipment?",
    helpText: "Data backup before moving media",
    category: "Physical Safeguards",
    subcategory: "Device and Media Controls",
    weight: 2,
  },

  // TECHNICAL SAFEGUARDS - Access Control (164.312(a))
  {
    id: "technical-1",
    text: "Do you assign unique user identifications for identifying and tracking user identity?",
    helpText: "No shared accounts or generic logins",
    orgHelpText: {
      medical: "Each physician, nurse, and staff member must have unique credentials",
      dental: "No shared 'front desk' logins - each staff member needs unique access",
      mental: "Critical for audit trails showing which therapist accessed which records",
      tech: "Include service accounts and API keys - each should be uniquely identifiable",
    },
    category: "Technical Safeguards",
    subcategory: "Access Control",
    weight: 3,
  },
  {
    id: "technical-2",
    text: "Do you have procedures for obtaining necessary ePHI access during emergencies?",
    helpText: "Emergency access procedures (break glass)",
    category: "Technical Safeguards",
    subcategory: "Access Control",
    weight: 2,
  },
  {
    id: "technical-3",
    text: "Do you have automatic logoff after a predetermined period of inactivity?",
    helpText: "Session timeout protects unattended workstations",
    orgHelpText: {
      medical: "Balance security with clinical workflow - typically 10-15 minutes",
      dental: "Consider treatment room flow when setting timeout duration",
      mental: "Shorter timeouts recommended given sensitivity of mental health records",
    },
    category: "Technical Safeguards",
    subcategory: "Access Control",
    weight: 2,
  },
  {
    id: "technical-4",
    text: "Do you encrypt ePHI when stored (data at rest)?",
    helpText: "Now mandatory under January 2025 updates",
    orgHelpText: {
      medical: "Include EHR databases, backup files, and portable devices",
      dental: "Include practice management databases and digital imaging storage",
      mental: "Essential for psychotherapy notes and session recordings",
      tech: "Include all customer data stores, backups, and development databases",
    },
    category: "Technical Safeguards",
    subcategory: "Access Control",
    weight: 3,
  },
  {
    id: "technical-5",
    text: "Do you implement Multi-Factor Authentication (MFA) for all ePHI access?",
    helpText: "Required under January 2025 Security Rule updates",
    orgHelpText: {
      medical: "Required for EHR access, patient portals (admin side), and remote access",
      dental: "Required for practice management system and any remote access",
      mental: "Critical for telehealth platforms and remote EHR access",
      tech: "Required for all production systems, cloud consoles, and customer data access",
    },
    category: "Technical Safeguards",
    subcategory: "Access Control",
    weight: 3,
  },

  // Audit Controls (164.312(b))
  {
    id: "technical-6",
    text: "Do you have mechanisms to record and examine activity in systems containing ePHI?",
    helpText: "Audit logs for all ePHI access and modifications",
    category: "Technical Safeguards",
    subcategory: "Audit Controls",
    weight: 3,
  },
  {
    id: "technical-7",
    text: "Do you retain audit logs for an appropriate period (typically 6 years)?",
    helpText: "HIPAA requires retention matching record retention policy",
    category: "Technical Safeguards",
    subcategory: "Audit Controls",
    weight: 2,
  },
  {
    id: "technical-8",
    text: "Do you regularly review audit logs for suspicious activity?",
    helpText: "Should be done at least monthly",
    category: "Technical Safeguards",
    subcategory: "Audit Controls",
    weight: 3,
  },

  // Integrity (164.312(c))
  {
    id: "technical-9",
    text: "Do you have policies and procedures to protect ePHI from improper alteration or destruction?",
    helpText: "Data integrity controls",
    category: "Technical Safeguards",
    subcategory: "Integrity",
    weight: 3,
  },
  {
    id: "technical-10",
    text: "Do you have mechanisms to corroborate that ePHI has not been altered or destroyed?",
    helpText: "Checksums, digital signatures, version control",
    category: "Technical Safeguards",
    subcategory: "Integrity",
    weight: 2,
  },

  // Person or Entity Authentication (164.312(d))
  {
    id: "technical-11",
    text: "Do you have procedures to verify that persons or entities seeking access to ePHI are who they claim to be?",
    helpText: "Authentication before granting access",
    category: "Technical Safeguards",
    subcategory: "Person or Entity Authentication",
    weight: 3,
  },

  // Transmission Security (164.312(e))
  {
    id: "technical-12",
    text: "Do you encrypt ePHI when transmitted over electronic networks?",
    helpText: "TLS/SSL for data in transit - now mandatory",
    orgHelpText: {
      medical: "Include data sent to labs, pharmacies, other providers, and patient portals",
      dental: "Include insurance claims, referrals, and patient communications",
      mental: "Critical for telehealth video sessions and patient messaging",
      tech: "Include all API communications, webhooks, and data exports",
    },
    category: "Technical Safeguards",
    subcategory: "Transmission Security",
    weight: 3,
  },
  {
    id: "technical-13",
    text: "Do you have integrity controls to ensure ePHI is not improperly modified during transmission?",
    helpText: "Checksums, digital signatures for transmitted data",
    category: "Technical Safeguards",
    subcategory: "Transmission Security",
    weight: 2,
  },
  {
    id: "technical-14",
    text: "Do you use secure email or encrypted messaging for sending ePHI?",
    helpText: "Standard email is not secure for ePHI",
    orgHelpText: {
      medical: "Use secure patient portals or encrypted email for lab results and communications",
      dental: "Avoid texting X-rays or treatment plans via standard SMS",
      mental: "Critical for appointment reminders and any clinical communications",
      tech: "Ensure customer support channels are encrypted when discussing PHI",
    },
    category: "Technical Safeguards",
    subcategory: "Transmission Security",
    weight: 3,
  },
  {
    id: "technical-15",
    text: "Do you have network segmentation to isolate systems containing ePHI?",
    helpText: "Now emphasized in January 2025 updates",
    orgHelpText: {
      medical: "Separate clinical systems from guest WiFi and general office network",
      dental: "Isolate practice management systems from public-facing networks",
      mental: "Ensure telehealth systems are on secured network segments",
      tech: "Implement VPCs, security groups, and micro-segmentation for ePHI systems",
    },
    category: "Technical Safeguards",
    subcategory: "Transmission Security",
    weight: 2,
  },

  // ORG-SPECIFIC QUESTIONS - Medical Practice
  {
    id: "medical-1",
    text: "Do you have security controls in place for networked medical devices (IoMT)?",
    helpText: "Includes patient monitors, infusion pumps, imaging equipment connected to the network",
    category: "Technical Safeguards",
    subcategory: "Medical Device Security",
    weight: 3,
    applicableTo: ["medical"],
  },
  {
    id: "medical-2",
    text: "Is your patient portal configured with appropriate security controls and access restrictions?",
    helpText: "Includes password requirements, session timeouts, and secure messaging",
    category: "Technical Safeguards",
    subcategory: "Patient Portal Security",
    weight: 3,
    applicableTo: ["medical"],
  },
  {
    id: "medical-3",
    text: "Do you have procedures for securely sharing patient information with other healthcare providers for care coordination?",
    helpText: "Includes referrals, consultations, and transitions of care",
    category: "Administrative Safeguards",
    subcategory: "Information Sharing",
    weight: 2,
    applicableTo: ["medical"],
  },
  {
    id: "medical-4",
    text: "Are clinical decision support systems and AI tools vetted for HIPAA compliance before implementation?",
    helpText: "Any AI/ML tools that process patient data must be compliant",
    category: "Administrative Safeguards",
    subcategory: "Technology Assessment",
    weight: 2,
    applicableTo: ["medical"],
  },

  // ORG-SPECIFIC QUESTIONS - Dental Practice
  {
    id: "dental-1",
    text: "Is your digital imaging system (X-rays, CBCT, intraoral cameras) secured with appropriate access controls?",
    helpText: "Includes storage, transmission, and viewing of dental images",
    category: "Technical Safeguards",
    subcategory: "Imaging Security",
    weight: 3,
    applicableTo: ["dental"],
  },
  {
    id: "dental-2",
    text: "Do you have secure procedures for transmitting insurance claims and patient information to payers?",
    helpText: "Electronic claims submission must use encrypted channels",
    category: "Technical Safeguards",
    subcategory: "Claims Security",
    weight: 2,
    applicableTo: ["dental"],
  },
  {
    id: "dental-3",
    text: "Is your practice management software configured with role-based access controls?",
    helpText: "Different access levels for dentists, hygienists, and front office staff",
    category: "Technical Safeguards",
    subcategory: "Access Control",
    weight: 3,
    applicableTo: ["dental"],
  },
  {
    id: "dental-4",
    text: "Do you have policies for securing patient information on tablets or mobile devices used chairside?",
    helpText: "Devices used during patient care must be secured",
    category: "Physical Safeguards",
    subcategory: "Mobile Device Security",
    weight: 2,
    applicableTo: ["dental"],
  },

  // ORG-SPECIFIC QUESTIONS - Mental Health
  {
    id: "mental-1",
    text: "Do you have enhanced protections for psychotherapy notes separate from general medical records?",
    helpText: "Psychotherapy notes have additional HIPAA protections and require separate authorization",
    category: "Administrative Safeguards",
    subcategory: "Psychotherapy Notes",
    weight: 3,
    applicableTo: ["mental"],
  },
  {
    id: "mental-2",
    text: "Is your telehealth platform HIPAA-compliant with BAA coverage?",
    helpText: "Video conferencing tools must be specifically designed for healthcare",
    category: "Technical Safeguards",
    subcategory: "Telehealth Security",
    weight: 3,
    applicableTo: ["mental"],
  },
  {
    id: "mental-3",
    text: "Do you have additional safeguards for substance abuse treatment records (42 CFR Part 2)?",
    helpText: "Substance abuse records have stricter federal protections than standard HIPAA",
    category: "Administrative Safeguards",
    subcategory: "Substance Abuse Records",
    weight: 3,
    applicableTo: ["mental"],
  },
  {
    id: "mental-4",
    text: "Do you have secure procedures for handling crisis situations while protecting patient privacy?",
    helpText: "Balancing duty to warn with confidentiality requirements",
    category: "Administrative Safeguards",
    subcategory: "Crisis Management",
    weight: 2,
    applicableTo: ["mental"],
  },
  {
    id: "mental-5",
    text: "Is your waiting room designed to protect patient privacy (visual and auditory)?",
    helpText: "Patients in mental health settings have heightened privacy concerns",
    category: "Physical Safeguards",
    subcategory: "Facility Privacy",
    weight: 2,
    applicableTo: ["mental"],
  },

  // ORG-SPECIFIC QUESTIONS - Healthcare Technology
  {
    id: "tech-1",
    text: "Do you have a secure software development lifecycle (SDLC) that includes security reviews?",
    helpText: "Security must be built into development, not added later",
    category: "Administrative Safeguards",
    subcategory: "Secure Development",
    weight: 3,
    applicableTo: ["tech"],
  },
  {
    id: "tech-2",
    text: "Are your APIs secured with authentication, authorization, and rate limiting?",
    helpText: "APIs are a common attack vector for healthcare data breaches",
    category: "Technical Safeguards",
    subcategory: "API Security",
    weight: 3,
    applicableTo: ["tech"],
  },
  {
    id: "tech-3",
    text: "Do you perform regular penetration testing and vulnerability assessments?",
    helpText: "Should be done at least annually and after major changes",
    category: "Technical Safeguards",
    subcategory: "Security Testing",
    weight: 3,
    applicableTo: ["tech"],
  },
  {
    id: "tech-4",
    text: "Do you have procedures for secure customer data offboarding when contracts end?",
    helpText: "Must securely delete or return all customer ePHI",
    category: "Administrative Safeguards",
    subcategory: "Data Lifecycle",
    weight: 3,
    applicableTo: ["tech"],
  },
  {
    id: "tech-5",
    text: "Do you maintain SOC 2 Type II or similar third-party security certifications?",
    helpText: "Industry-standard certification demonstrates security commitment",
    category: "Administrative Safeguards",
    subcategory: "Certifications",
    weight: 2,
    applicableTo: ["tech"],
  },
  {
    id: "tech-6",
    text: "Do you have a responsible disclosure/bug bounty program for security researchers?",
    helpText: "Allows external researchers to report vulnerabilities safely",
    category: "Administrative Safeguards",
    subcategory: "Vulnerability Management",
    weight: 1,
    applicableTo: ["tech"],
  },
]

// Function to filter and sort questions based on org type
function getQuestionsForOrg(orgType: OrgType | null): Question[] {
  // If no org type, return all base questions (excluding org-specific ones)
  if (!orgType) {
    return allQuestions.filter(q => !q.applicableTo || q.applicableTo.length === 0)
  }

  const config = orgTypeConfig[orgType]

  // Filter questions
  let filtered = allQuestions.filter(q => {
    // Exclude if in org's exclude list
    if (config.excludeQuestions.includes(q.id)) return false

    // Exclude if question specifies it should be excluded from this org
    if (q.excludeFrom?.includes(orgType)) return false

    // Include if question is for all orgs (no applicableTo) or specifically for this org
    if (!q.applicableTo || q.applicableTo.length === 0) return true
    return q.applicableTo.includes(orgType)
  })

  // Sort by priority
  filtered.sort((a, b) => {
    const aIsPriority = config.priorityQuestions.some(p => a.id.startsWith(p) || a.id === p)
    const bIsPriority = config.priorityQuestions.some(p => b.id.startsWith(p) || b.id === p)

    if (aIsPriority && !bIsPriority) return -1
    if (!aIsPriority && bIsPriority) return 1
    return 0
  })

  return filtered
}

// Get help text for a question, using org-specific version if available
function getHelpText(question: Question, orgType: OrgType | null): string | undefined {
  if (orgType && question.orgHelpText?.[orgType]) {
    return question.orgHelpText[orgType]
  }
  return question.helpText
}

// Calculate scores by category
function calculateScores(answers: Answer[], questions: Question[]): {
  overall: number
  categories: CategoryScore[]
  riskLevel: "low" | "medium" | "high" | "critical"
  gaps: string[]
} {
  const categoryMap: Record<string, { total: number; earned: number; gaps: string[] }> = {
    "Administrative Safeguards": { total: 0, earned: 0, gaps: [] },
    "Physical Safeguards": { total: 0, earned: 0, gaps: [] },
    "Technical Safeguards": { total: 0, earned: 0, gaps: [] },
  }

  const allGaps: string[] = []

  questions.forEach((q) => {
    const answer = answers.find((a) => a.questionId === q.id)
    const maxPoints = q.weight * 3

    categoryMap[q.category].total += maxPoints

    if (answer) {
      let earned = 0
      switch (answer.value) {
        case "yes":
          earned = maxPoints
          break
        case "partial":
          earned = maxPoints * 0.5
          break
        case "not_sure":
          earned = maxPoints * 0.25
          categoryMap[q.category].gaps.push(q.text)
          allGaps.push(q.text)
          break
        case "no":
          earned = 0
          categoryMap[q.category].gaps.push(q.text)
          allGaps.push(q.text)
          break
      }
      categoryMap[q.category].earned += earned
    }
  })

  const categories: CategoryScore[] = Object.entries(categoryMap).map(([name, data]) => ({
    name,
    score: Math.round(data.earned),
    maxScore: data.total,
    percentage: data.total > 0 ? Math.round((data.earned / data.total) * 100) : 0,
    gaps: data.gaps,
  }))

  const totalEarned = categories.reduce((sum, c) => sum + c.score, 0)
  const totalMax = categories.reduce((sum, c) => sum + c.maxScore, 0)
  const overall = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : 0

  let riskLevel: "low" | "medium" | "high" | "critical"
  if (overall >= 80) riskLevel = "low"
  else if (overall >= 60) riskLevel = "medium"
  else if (overall >= 40) riskLevel = "high"
  else riskLevel = "critical"

  return { overall, categories, riskLevel, gaps: allGaps }
}

function SelfAssessmentContent() {
  const searchParams = useSearchParams()
  const orgTypeParam = searchParams.get("org")
  const orgType = orgTypeParam && orgTypeParam in orgTypeConfig ? orgTypeParam as OrgType : null
  const orgConfig = orgType ? orgTypeConfig[orgType] : null

  // Get filtered/sorted questions for this org type
  const questions = useMemo(() => getQuestionsForOrg(orgType), [orgType])

  const [step, setStep] = useState<"intro" | "questions" | "contact" | "results">("intro")
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState<Answer[]>([])
  const [contactInfo, setContactInfo] = useState({
    name: "",
    email: "",
    organization: "",
    phone: "",
  })
  const [activeResultsTab, setActiveResultsTab] = useState<"administrative" | "physical" | "technical">("administrative")

  const currentQuestion = questions[currentQuestionIndex]
  const progress = Math.round((currentQuestionIndex / questions.length) * 100)

  const handleAnswer = (value: "yes" | "no" | "partial" | "not_sure") => {
    const existingIndex = answers.findIndex((a) => a.questionId === currentQuestion.id)
    const newAnswer: Answer = { questionId: currentQuestion.id, value }

    if (existingIndex >= 0) {
      const newAnswers = [...answers]
      newAnswers[existingIndex] = newAnswer
      setAnswers(newAnswers)
    } else {
      setAnswers([...answers, newAnswer])
    }

    // Auto-advance to next question
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    } else {
      setStep("contact")
    }
  }

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const scores = calculateScores(answers, questions)
    // Submit assessment results to D1 (fire-and-forget)
    fetch("/api/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        formType: "assessment",
        name: contactInfo.name,
        email: contactInfo.email,
        company: contactInfo.organization,
        phone: contactInfo.phone,
        formData: {
          overallScore: scores.overall,
          riskLevel: scores.riskLevel,
          categories: scores.categories.map(c => ({
            name: c.name,
            percentage: c.percentage,
            score: c.score,
            maxScore: c.maxScore,
          })),
          gapCount: scores.gaps.length,
          orgType: orgType || "general",
        },
      }),
    }).catch(() => {})
    setStep("results")
  }

  const results = calculateScores(answers, questions)

  // Helper to get compliance items by category
  const getComplianceItemsByCategory = (categoryKey: "administrative" | "physical" | "technical") => {
    const categoryMap: Record<string, string> = {
      administrative: "Administrative Safeguards",
      physical: "Physical Safeguards",
      technical: "Technical Safeguards",
    }
    const categoryName = categoryMap[categoryKey]
    const categoryQuestions = questions.filter(q => q.category === categoryName)

    const compliant: { question: Question; answer: Answer | undefined }[] = []
    const nonCompliant: { question: Question; answer: Answer | undefined }[] = []
    const partial: { question: Question; answer: Answer | undefined }[] = []

    categoryQuestions.forEach(q => {
      const answer = answers.find(a => a.questionId === q.id)
      if (answer?.value === "yes") {
        compliant.push({ question: q, answer })
      } else if (answer?.value === "partial") {
        partial.push({ question: q, answer })
      } else {
        nonCompliant.push({ question: q, answer })
      }
    })

    return { compliant, nonCompliant, partial, total: categoryQuestions.length }
  }

  const getRiskColor = (level: string) => {
    switch (level) {
      case "low":
        return "text-green-600 bg-green-100"
      case "medium":
        return "text-yellow-600 bg-yellow-100"
      case "high":
        return "text-orange-600 bg-orange-100"
      case "critical":
        return "text-red-600 bg-red-100"
      default:
        return "text-gray-600 bg-gray-100"
    }
  }

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return "text-green-600"
    if (percentage >= 60) return "text-yellow-700"
    if (percentage >= 40) return "text-orange-600"
    return "text-red-600"
  }

  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return "bg-green-500"
    if (percentage >= 60) return "bg-yellow-500"
    if (percentage >= 40) return "bg-orange-500"
    return "bg-red-500"
  }

  // Count questions by category for this org type
  const adminCount = questions.filter(q => q.category === "Administrative Safeguards").length
  const physicalCount = questions.filter(q => q.category === "Physical Safeguards").length
  const technicalCount = questions.filter(q => q.category === "Technical Safeguards").length

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Intro Step */}
      {step === "intro" && (
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Badge className="bg-[#E0F2FE] text-[#0284C7] mb-4">
                {orgConfig ? orgConfig.badge : "Free HIPAA Self-Assessment"}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">
                {orgConfig ? orgConfig.headline : "HIPAA Security Rule Compliance Assessment"}
              </h1>
              <p className="text-lg text-[#64748B] mb-8">
                {orgConfig
                  ? orgConfig.description
                  : `Answer ${questions.length} questions across Administrative, Physical, and Technical Safeguards to understand your HIPAA compliance posture. Takes approximately 10-15 minutes.`
                }
              </p>

              {/* Org-specific key focus areas */}
              {orgConfig && (
                <Card className="border-2 border-[#10B981] bg-[#F0FDF4] mb-8">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <orgConfig.icon className="h-6 w-6 text-[#10B981]" />
                      <h2 className="text-xl font-bold text-[#0F172A]">Key Focus Areas for {orgConfig.title}</h2>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-left">
                      {orgConfig.keyFocus.map((focus, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-[#10B981] flex-shrink-0" />
                          <span className="text-sm text-[#374151]">{focus}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="border-2 border-[#0EA5E9] mb-8">
                <CardContent className="pt-6">
                  <h2 className="text-xl font-bold text-[#0F172A] mb-4">What This Assessment Covers</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
                        <FileCheck className="h-5 w-5 text-[#0EA5E9]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0F172A]">Administrative</h3>
                        <p className="text-sm text-[#64748B]">{adminCount} questions covering policies, training, and security management</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-[#0EA5E9]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0F172A]">Physical</h3>
                        <p className="text-sm text-[#64748B]">{physicalCount} questions on facility access and device controls</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
                        <Monitor className="h-5 w-5 text-[#0EA5E9]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#0F172A]">Technical</h3>
                        <p className="text-sm text-[#64748B]">{technicalCount} questions on access control, encryption, and audit controls</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-[#FEF3C7] border border-[#F59E0B] rounded-lg p-4 mb-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#D97706] flex-shrink-0 mt-0.5" />
                  <div className="text-left">
                    <p className="font-bold text-[#92400E]">January 2025 Updates Included</p>
                    <p className="text-sm text-[#92400E]">This assessment includes questions about the new mandatory MFA, encryption, and 72-hour breach notification requirements.</p>
                  </div>
                </div>
              </div>

              <Button
                size="lg"
                className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-lg h-14 px-8"
                onClick={() => setStep("questions")}
              >
                Start Assessment <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <p className="text-sm text-[#64748B] mt-4">
                Your responses are confidential and encrypted. Results are provided instantly.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Questions Step */}
      {step === "questions" && (
        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              {/* Progress Header */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#64748B]">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <span className="text-sm font-medium text-[#0EA5E9]">{progress}% Complete</span>
                </div>
                <Progress value={progress} className="h-2" />
                <div className="mt-2 flex flex-wrap gap-2">
                  <Badge className="bg-[#E0F2FE] text-[#0284C7]">
                    {currentQuestion.category} - {currentQuestion.subcategory}
                  </Badge>
                  {orgConfig && currentQuestion.id.startsWith(orgType + "-") && (
                    <Badge className="bg-[#10B981] text-white">
                      {orgConfig.title} Specific
                    </Badge>
                  )}
                </div>
              </div>

              {/* Question Card */}
              <Card className="border-2 border-[#E2E8F0] mb-6">
                <CardContent className="pt-8 pb-8">
                  {/* Fixed height container for question text to prevent button jumping */}
                  <div className="min-h-[140px] mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-4">
                      {currentQuestion.text}
                    </h2>
                    {getHelpText(currentQuestion, orgType) && (
                      <p className="text-sm text-[#64748B] flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        {getHelpText(currentQuestion, orgType)}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Button
                      variant="outline"
                      className={`h-16 flex flex-col items-center justify-center gap-1 ${
                        answers.find((a) => a.questionId === currentQuestion.id)?.value === "yes"
                          ? "border-green-500 bg-green-50 text-green-700"
                          : "hover:border-green-500 hover:bg-green-50"
                      }`}
                      onClick={() => handleAnswer("yes")}
                    >
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="text-sm font-medium">Yes</span>
                    </Button>
                    <Button
                      variant="outline"
                      className={`h-16 flex flex-col items-center justify-center gap-1 ${
                        answers.find((a) => a.questionId === currentQuestion.id)?.value === "partial"
                          ? "border-yellow-500 bg-yellow-50 text-yellow-700"
                          : "hover:border-yellow-500 hover:bg-yellow-50"
                      }`}
                      onClick={() => handleAnswer("partial")}
                    >
                      <RefreshCw className="h-5 w-5" />
                      <span className="text-sm font-medium">Partial</span>
                    </Button>
                    <Button
                      variant="outline"
                      className={`h-16 flex flex-col items-center justify-center gap-1 ${
                        answers.find((a) => a.questionId === currentQuestion.id)?.value === "no"
                          ? "border-red-500 bg-red-50 text-red-700"
                          : "hover:border-red-500 hover:bg-red-50"
                      }`}
                      onClick={() => handleAnswer("no")}
                    >
                      <X className="h-5 w-5" />
                      <span className="text-sm font-medium">No</span>
                    </Button>
                    <Button
                      variant="outline"
                      className={`h-16 flex flex-col items-center justify-center gap-1 ${
                        answers.find((a) => a.questionId === currentQuestion.id)?.value === "not_sure"
                          ? "border-gray-500 bg-gray-50 text-gray-700"
                          : "hover:border-gray-500 hover:bg-gray-50"
                      }`}
                      onClick={() => handleAnswer("not_sure")}
                    >
                      <AlertCircle className="h-5 w-5" />
                      <span className="text-sm font-medium">Not Sure</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" /> Previous
                </Button>
                <span className="text-sm text-[#64748B]">
                  {answers.length} of {questions.length} answered
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Step */}
      {step === "contact" && (
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-xl mx-auto">
              <div className="text-center mb-8">
                <CheckCircle2 className="h-16 w-16 text-[#10B981] mx-auto mb-4" />
                <h1 className="text-3xl font-bold text-[#0F172A] mb-2">Assessment Complete!</h1>
                <p className="text-[#64748B]">
                  Enter your details to receive your personalized compliance report.
                </p>
              </div>

              <Card className="border-2 border-[#0EA5E9]">
                <CardContent className="pt-6">
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-1">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={contactInfo.name}
                        onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                        className="w-full px-4 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-1">Work Email *</label>
                      <input
                        type="email"
                        required
                        value={contactInfo.email}
                        onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                        className="w-full px-4 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                        placeholder="john@healthcare.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-1">Organization Name *</label>
                      <input
                        type="text"
                        required
                        value={contactInfo.organization}
                        onChange={(e) => setContactInfo({ ...contactInfo, organization: e.target.value })}
                        className="w-full px-4 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                        placeholder="ABC Healthcare"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-1">Phone (Optional)</label>
                      <input
                        type="tel"
                        value={contactInfo.phone}
                        onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                        className="w-full px-4 py-2 border border-[#E2E8F0] rounded-lg focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-lg h-14"
                    >
                      View My Results <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                  <p className="text-xs text-[#64748B] mt-4 text-center">
                    Your information is kept confidential and will only be used to send your compliance report.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Results Step */}
      {step === "results" && (
        <section className="py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Overall Score */}
              <Card className="border-2 border-[#E2E8F0] mb-8">
                <CardContent className="pt-8 pb-8">
                  <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-[#0F172A] mb-2">
                      {contactInfo.organization}&apos;s HIPAA Compliance Score
                    </h1>
                    {orgConfig && (
                      <Badge className="bg-[#E0F2FE] text-[#0284C7] mb-2">
                        {orgConfig.title} Assessment
                      </Badge>
                    )}
                    <div className="relative inline-flex items-center justify-center w-48 h-48 my-6">
                      <svg className="w-full h-full" viewBox="0 0 100 100">
                        <circle
                          className="text-gray-200"
                          strokeWidth="8"
                          stroke="currentColor"
                          fill="transparent"
                          r="42"
                          cx="50"
                          cy="50"
                        />
                        <circle
                          className={getScoreColor(results.overall).replace("text-", "text-")}
                          strokeWidth="8"
                          strokeDasharray={`${results.overall * 2.64} 264`}
                          strokeLinecap="round"
                          stroke="currentColor"
                          fill="transparent"
                          r="42"
                          cx="50"
                          cy="50"
                          style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
                        />
                      </svg>
                      <div className="absolute flex flex-col items-center">
                        <span className={`text-4xl font-bold ${getScoreColor(results.overall)}`}>{results.overall}%</span>
                        <span className="text-sm text-[#64748B]">Overall Score</span>
                      </div>
                    </div>
                    <Badge className={`text-lg px-4 py-2 ${getRiskColor(results.riskLevel)}`}>
                      {results.riskLevel === "low" && "Low Risk"}
                      {results.riskLevel === "medium" && "Medium Risk"}
                      {results.riskLevel === "high" && "High Risk"}
                      {results.riskLevel === "critical" && "Critical Risk"}
                    </Badge>
                  </div>

                  {/* Category Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {results.categories.map((category) => (
                      <Card key={category.name} className="border border-[#E2E8F0]">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-[#64748B]">{category.name}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className={`text-3xl font-bold ${getScoreColor(category.percentage)}`}>
                            {category.percentage}%
                          </div>
                          <Progress
                            value={category.percentage}
                            className={`h-2 mt-2 ${getProgressColor(category.percentage)}`}
                          />
                          <p className="text-xs text-[#64748B] mt-2">
                            {category.gaps.length} gap{category.gaps.length !== 1 ? "s" : ""} identified
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Tabbed Compliance Details */}
                  <div className="mb-8">
                    <h3 className="font-bold text-[#0F172A] mb-4">Detailed Compliance Status</h3>

                    {/* Tab Buttons */}
                    <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                      <button
                        onClick={() => setActiveResultsTab("administrative")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                          activeResultsTab === "administrative"
                            ? "bg-[#0EA5E9] text-white"
                            : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"
                        }`}
                      >
                        <FileCheck className="h-4 w-4" />
                        Administrative ({adminCount})
                      </button>
                      <button
                        onClick={() => setActiveResultsTab("physical")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                          activeResultsTab === "physical"
                            ? "bg-[#0EA5E9] text-white"
                            : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"
                        }`}
                      >
                        <Building2 className="h-4 w-4" />
                        Physical ({physicalCount})
                      </button>
                      <button
                        onClick={() => setActiveResultsTab("technical")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                          activeResultsTab === "technical"
                            ? "bg-[#0EA5E9] text-white"
                            : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"
                        }`}
                      >
                        <Monitor className="h-4 w-4" />
                        Technical ({technicalCount})
                      </button>
                    </div>

                    {/* Tab Content */}
                    {(() => {
                      const { compliant, nonCompliant, partial } = getComplianceItemsByCategory(activeResultsTab)
                      const categoryLabel = activeResultsTab === "administrative" ? "Administrative" : activeResultsTab === "physical" ? "Physical" : "Technical"

                      return (
                        <div className="space-y-6">
                          {/* Summary for this category */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-lg p-4 text-center">
                              <div className="text-2xl font-bold text-[#16A34A]">{compliant.length}</div>
                              <div className="text-xs text-[#15803D]">Compliant</div>
                            </div>
                            <div className="bg-[#FEF9C3] border border-[#FDE047] rounded-lg p-4 text-center">
                              <div className="text-2xl font-bold text-[#CA8A04]">{partial.length}</div>
                              <div className="text-xs text-[#A16207]">Partial</div>
                            </div>
                            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4 text-center">
                              <div className="text-2xl font-bold text-[#DC2626]">{nonCompliant.length}</div>
                              <div className="text-xs text-[#B91C1C]">Non-Compliant</div>
                            </div>
                          </div>

                          {/* Non-Compliant Items */}
                          {nonCompliant.length > 0 && (
                            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4">
                              <h4 className="font-bold text-[#DC2626] mb-3 flex items-center gap-2">
                                <X className="h-5 w-5" />
                                Gaps to Address ({nonCompliant.length})
                              </h4>
                              <ul className="space-y-2">
                                {nonCompliant.map(({ question }) => (
                                  <li key={question.id} className="flex items-start gap-2 text-sm text-[#B91C1C]">
                                    <X className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#DC2626]" />
                                    <span>{question.text}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Partial Items */}
                          {partial.length > 0 && (
                            <div className="bg-[#FEF9C3] border border-[#FDE047] rounded-lg p-4">
                              <h4 className="font-bold text-[#CA8A04] mb-3 flex items-center gap-2">
                                <RefreshCw className="h-5 w-5" />
                                In Progress ({partial.length})
                              </h4>
                              <ul className="space-y-2">
                                {partial.map(({ question }) => (
                                  <li key={question.id} className="flex items-start gap-2 text-sm text-[#A16207]">
                                    <RefreshCw className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#CA8A04]" />
                                    <span>{question.text}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Compliant Items */}
                          {compliant.length > 0 && (
                            <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-lg p-4">
                              <h4 className="font-bold text-[#16A34A] mb-3 flex items-center gap-2">
                                <CheckCircle2 className="h-5 w-5" />
                                What You&apos;re Doing Well ({compliant.length})
                              </h4>
                              <ul className="space-y-2">
                                {compliant.map(({ question }) => (
                                  <li key={question.id} className="flex items-start gap-2 text-sm text-[#15803D]">
                                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#16A34A]" />
                                    <span>{question.text}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* No gaps message */}
                          {nonCompliant.length === 0 && partial.length === 0 && (
                            <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-lg p-6 text-center">
                              <CheckCircle2 className="h-12 w-12 text-[#16A34A] mx-auto mb-2" />
                              <p className="font-bold text-[#16A34A]">Excellent! No gaps in {categoryLabel} Safeguards!</p>
                              <p className="text-sm text-[#15803D]">You&apos;re fully compliant in this category.</p>
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                </CardContent>
              </Card>

              {/* HIPAA Ready Pro Upsell */}
              <Card className="border-2 border-[#0EA5E9] bg-gradient-to-br from-[#F0F9FF] to-white mb-8">
                <CardContent className="pt-8 pb-8">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-1">
                      <Badge className="bg-[#10B981] text-white mb-4">Recommended Next Step</Badge>
                      <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
                        Upgrade to HIPAA Ready Pro
                      </h2>
                      <p className="text-[#64748B] mb-6">
                        Based on your assessment results, we recommend our comprehensive AI-powered compliance management platform. Get continuous compliance monitoring, automated policy generation, and expert vCISO guidance—reducing manual compliance work by up to 70%.
                      </p>

                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
                            <ClipboardCheck className="h-4 w-4 text-[#0EA5E9]" />
                          </div>
                          <div>
                            <h4 className="font-bold text-[#0F172A] text-sm">Automated Risk Analysis</h4>
                            <p className="text-xs text-[#64748B]">AI-driven continuous monitoring & documentation</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
                            <FileCheck className="h-4 w-4 text-[#0EA5E9]" />
                          </div>
                          <div>
                            <h4 className="font-bold text-[#0F172A] text-sm">Policy Generation</h4>
                            <p className="text-xs text-[#64748B]">Auto-generated HIPAA policies & procedures</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
                            <Zap className="h-4 w-4 text-[#0EA5E9]" />
                          </div>
                          <div>
                            <h4 className="font-bold text-[#0F172A] text-sm">Virtual CISO Support</h4>
                            <p className="text-xs text-[#64748B]">AI-powered strategic security guidance</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
                            <Target className="h-4 w-4 text-[#0EA5E9]" />
                          </div>
                          <div>
                            <h4 className="font-bold text-[#0F172A] text-sm">Gap Remediation</h4>
                            <p className="text-xs text-[#64748B]">Step-by-step compliance roadmap</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mb-6">
                        <div className="text-3xl font-bold text-[#0F172A]">$3,500<span className="text-lg font-normal text-[#64748B]">/year</span></div>
                        <div className="text-sm text-[#64748B]">
                          <span className="line-through">$5,000/year</span>
                          <Badge className="ml-2 bg-[#10B981] text-white">30% Off First Year</Badge>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3">
                        <a href="/hipaa-ready-pro#schedule-demo" className="flex-1">
                          <Button size="lg" className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white w-full">
                            Schedule Demo <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </a>
                        <a href="/hipaa-ready-pro" className="flex-1">
                          <Button size="lg" variant="outline" className="w-full">
                            Learn More
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Alternative Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border border-[#E2E8F0]">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-[#0F172A] mb-2">Download Your Compliance Checklist</h3>
                    <p className="text-sm text-[#64748B] mb-4">
                      Get a personalized PDF checklist showing your compliance status for each requirement.
                    </p>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        generateComplianceChecklistPDF({
                          contactInfo,
                          orgType,
                          orgTitle: orgConfig?.title || "General",
                          results,
                          questions,
                          answers,
                          assessmentDate: new Date(),
                        })
                      }}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF Checklist
                    </Button>
                  </CardContent>
                </Card>
                <Card className="border border-[#E2E8F0]">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-[#0F172A] mb-2">Schedule Consultation</h3>
                    <p className="text-sm text-[#64748B] mb-4">
                      Speak with a HIPAA compliance expert to review your results.
                    </p>
                    <Button variant="outline" className="w-full">
                      Book Free Consultation
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  )
}

export default function SelfAssessmentPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-pulse text-[#0EA5E9]">Loading...</div></div>}>
      <SelfAssessmentContent />
    </Suspense>
  )
}
